if (!customElements.get('easymeeple-box-wizard')) {
  customElements.define(
    'easymeeple-box-wizard',
    class EasyMeepleBoxWizard extends HTMLElement {
      connectedCallback() {
        this.config = JSON.parse(this.querySelector('[data-easymeeple-config]').textContent);
        this.config.passProductIds = (this.config.passProductIds || []).filter(Boolean).map(String);
        this.config.passVariantIds = (this.config.passVariantIds || []).filter(Boolean).map(String);
        this.config.depositProductId = String(this.config.depositProductId || '');
        this.config.depositVariantId = String(this.config.depositVariantId || '');
        this.config.placeholderVariantId = String(this.config.placeholderVariantId || '');
        this.config.placeholderVariantIds = (this.config.placeholderVariantIds || [this.config.placeholderVariantId])
          .filter(Boolean)
          .map(String);
        this.config.boxItemProductIds = (this.config.boxItemProductIds || []).filter(Boolean).map(String);
        this.config.boxItemsCollectionUrl = String(this.config.boxItemsCollectionUrl || '');
        this.boxItemProductIds = new Set(this.config.boxItemProductIds);
        this.config.boxProductId = String(this.config.boxProductId || '');
        this.config.boxVariants = Object.fromEntries(
          Object.entries(this.config.boxVariants || {})
            .filter(([, variantId]) => variantId)
            .map(([count, variantId]) => [String(count), String(variantId)])
        );
        this.config.mode = this.config.mode === 'box_swap' ? 'box_swap' : 'new_customer';
        this.availableSteps = (this.config.availableSteps || [1, 2, 3, 4])
          .map(Number)
          .filter((step) => [1, 2, 3, 4].includes(step));
        if (!this.availableSteps.length) this.availableSteps = [1, 2, 3, 4];
        this.panels = Array.from(this.querySelectorAll('[data-wizard-panel]'));
        this.progressButtons = Array.from(this.querySelectorAll('[data-wizard-step]'));
        this.productGridSection = document.querySelector('[id$="__product-grid"]');
        this.currentStep = this.availableSteps[0];
        this.cart = null;
        this.passCardSyncFrame = null;
        this.handlePassCardResize = this.schedulePassCardRowSync.bind(this);
        this.handleBoxSynced = this.onBoxSynced.bind(this);
        this.handleBoxSyncError = this.onBoxSyncError.bind(this);
        this.handleProductGridClick = this.onProductGridClick.bind(this);
        this.handleProductGridSubmit = this.onProductGridSubmit.bind(this);
        this.handlePageShow = this.onPageShow.bind(this);

        this.addEventListener('click', this.onClick.bind(this));
        this.productGridSection?.addEventListener('click', this.handleProductGridClick, true);
        this.productGridSection?.addEventListener('submit', this.handleProductGridSubmit, true);
        window.addEventListener('resize', this.handlePassCardResize);
        document.addEventListener('easymeeple:box-synced', this.handleBoxSynced);
        document.addEventListener('easymeeple:box-sync-error', this.handleBoxSyncError);
        document.fonts?.ready.then(() => this.schedulePassCardRowSync());
        this.bindCartEvents();
        this.initialize();
      }

      disconnectedCallback() {
        if (this.unsubscribeCart) this.unsubscribeCart();
        window.removeEventListener('resize', this.handlePassCardResize);
        window.removeEventListener('pageshow', this.handlePageShow);
        document.removeEventListener('easymeeple:box-synced', this.handleBoxSynced);
        document.removeEventListener('easymeeple:box-sync-error', this.handleBoxSyncError);
        this.productGridSection?.removeEventListener('click', this.handleProductGridClick, true);
        this.productGridSection?.removeEventListener('submit', this.handleProductGridSubmit, true);
        if (this.passCardSyncFrame) cancelAnimationFrame(this.passCardSyncFrame);
      }

      get storageKey() {
        return `easymeeple-wizard-step-${this.dataset.sectionId}-${this.config.mode}`;
      }

      async initialize() {
        await this.loadBoxItemProductIds();
        await this.refreshCart();

        this.currentStep = this.getCartBasedStartStep();
        this.setStep(this.currentStep, false);
        this.schedulePassCardRowSync();
        document.documentElement.classList.remove('easymeeple-wizard-page');
      }

      getCartBasedStartStep() {
        if (this.config.mode === 'box_swap') return 3;
        if (!this.summary || this.summary.passCount === 0) return 1;
        if (this.summary.depositCount === 0) return 2;
        return 3;
      }

      async onPageShow() {
        await this.refreshCart();
        this.setStep(this.getCartBasedStartStep(), false);
      }

      bindCartEvents() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
            await this.refreshCart();
            const variantId = String(event?.productVariantId || '');

            const wizardAdd = event?.source === 'easymeeple-wizard' && this.config.mode === 'new_customer';
            if (wizardAdd) this.advanceAfterWizardAdd(variantId);
          });
        }

        document.addEventListener('easymeeple:cart-updated', () => this.refreshCart());
        window.addEventListener('pageshow', this.handlePageShow);
      }

      onBoxSynced(event) {
        const cart = event?.detail?.cart;
        if (!cart) return;
        this.cart = cart;
        this.summary = this.summarizeCart(cart);
        this.renderCartState();
      }

      onBoxSyncError(event) {
        const status = this.querySelector('[data-wizard-status]');
        if (status) status.textContent = event?.detail?.error?.message || this.config.boxSyncError;
      }

      async loadBoxItemProductIds() {
        if (!this.config.boxItemsCollectionUrl) return;

        const pageSize = 250;
        const maximumPages = 20;

        try {
          for (let page = 1; page <= maximumPages; page += 1) {
            const endpoint = new URL(
              `${this.config.boxItemsCollectionUrl.replace(/\/$/, '')}/products.json`,
              window.location.origin
            );
            endpoint.searchParams.set('limit', String(pageSize));
            endpoint.searchParams.set('page', String(page));

            const response = await fetch(endpoint.toString(), {
              headers: { Accept: 'application/json' },
            });
            if (!response.ok) throw new Error('Could not load the box-items collection');

            const data = await response.json();
            const products = Array.isArray(data.products) ? data.products : [];
            const previousSize = this.boxItemProductIds.size;
            products.forEach((product) => this.boxItemProductIds.add(String(product.id)));

            if (products.length < pageSize || this.boxItemProductIds.size === previousSize) break;
          }
        } catch (error) {
          console.error('Could not refresh the box-item product lookup.', error);
        }
      }

      async refreshCart() {
        this.classList.add('easymeeple-wizard__loading');

        try {
          const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('Could not load cart');
          this.cart = await response.json();
          this.summary = this.summarizeCart(this.cart);
          this.renderCartState();
        } catch (error) {
          const status = this.querySelector('[data-wizard-status]');
          if (status) status.textContent = this.config.cartLoadError;
          console.error(error);
        } finally {
          this.classList.remove('easymeeple-wizard__loading');
        }
      }

      summarizeCart(cart) {
        const summary = {
          passCount: 0,
          depositCount: 0,
          gameCount: 0,
          boxQuantity: 0,
          boxLines: [],
          duplicateGameTitles: [],
          warnings: [],
        };
        const gameVariants = new Map();

        cart.items.forEach((item) => {
          const productId = String(item.product_id);
          const variantId = String(item.variant_id);
          const quantity = Number(item.quantity) || 0;

          if (this.config.passProductIds.includes(productId)) summary.passCount += quantity;
          if (productId === this.config.depositProductId) summary.depositCount += quantity;
          if (productId === this.config.boxProductId) {
            summary.boxQuantity += quantity;
            summary.boxLines.push(item);
          }

          if (this.isGame(item)) {
            summary.gameCount += quantity;
            gameVariants.set(variantId, {
              title: item.product_title,
              quantity: (gameVariants.get(variantId)?.quantity || 0) + quantity,
            });
          }
        });

        gameVariants.forEach((value, variantId) => {
          if (value.quantity > 1 && !this.config.placeholderVariantIds.includes(variantId)) {
            summary.duplicateGameTitles.push(value.title);
          }
        });

        if (this.config.mode === 'new_customer') {
          if (summary.passCount === 0) summary.warnings.push(this.config.warningMissingPass);
          if (summary.passCount > 1) summary.warnings.push(this.config.warningMultiplePasses);
          if (summary.depositCount === 0) summary.warnings.push(this.config.warningMissingDeposit);
          if (summary.depositCount > 1) summary.warnings.push(this.config.warningMultipleDeposits);
        }
        if (summary.gameCount === 0) summary.warnings.push(this.config.warningNoGames);
        if (summary.gameCount > this.config.maxGames) summary.warnings.push(this.config.warningTooManyGames);
        if (summary.gameCount >= 1 && summary.gameCount <= this.config.maxGames) {
          const desiredBoxVariantId = String(this.config.boxVariants[String(summary.gameCount)] || '');
          const correctBox =
            summary.boxLines.length === 1 &&
            summary.boxQuantity === 1 &&
            String(summary.boxLines[0].variant_id) === desiredBoxVariantId;
          if (summary.boxQuantity === 0) summary.warnings.push(this.config.warningMissingBox);
          else if (summary.boxQuantity > 1 || summary.boxLines.length > 1) {
            summary.warnings.push(this.config.warningMultipleBoxes);
          } else if (!correctBox) {
            summary.warnings.push(this.config.warningWrongBox);
          }
        } else if (summary.boxQuantity > 0) {
          summary.warnings.push(this.config.warningWrongBox);
        }
        summary.duplicateGameTitles.forEach((title) => {
          summary.warnings.push(this.config.warningDuplicateVariant.replace('[game]', title));
        });

        return summary;
      }

      isGame(item) {
        if (this.config.placeholderVariantIds.includes(String(item.variant_id))) return true;
        return this.boxItemProductIds.has(String(item.product_id));
      }

      onClick(event) {
        const picksQuantityButton = event.target.closest('[data-picks-quantity-change]');
        if (picksQuantityButton) {
          event.preventDefault();
          this.changePicksQuantity(Number(picksQuantityButton.dataset.picksQuantityChange));
          return;
        }

        const picksAddButton = event.target.closest('[data-picks-add]');
        if (picksAddButton) {
          event.preventDefault();
          this.addPicks(picksAddButton);
          return;
        }

        const addButton = event.target.closest('[data-wizard-add]');
        if (addButton) {
          event.preventDefault();
          this.addItem(addButton);
          return;
        }

        const checkoutLink = event.target.closest('[data-wizard-checkout]');
        if (checkoutLink) {
          event.preventDefault();
          this.continueToCheckout(checkoutLink);
          return;
        }

        const stepButton = event.target.closest('[data-wizard-step]');
        if (stepButton) {
          this.setStep(Number(stepButton.dataset.wizardStep));
          return;
        }

        const action = event.target.closest('[data-wizard-action]');
        if (!action) return;

        if (action.dataset.wizardAction === 'back') this.moveStep(-1);
        if (action.dataset.wizardAction === 'next') this.moveStep(1);
        if (action.dataset.wizardAction === 'refresh') this.refreshCart();
      }

      onProductGridSubmit(event) {
        const form = event.target.closest('form[action*="/cart/add"]');
        if (!form || !this.productGridSection?.contains(form)) return;

        const button = event.submitter || form.querySelector('button[type="submit"]');
        const variantInput = form.querySelector('[name="id"]');
        if (!button || !variantInput?.value) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        button.dataset.variantId = variantInput.value;
        this.addItem(button);
      }

      onProductGridClick(event) {
        const button = event.target.closest('form[action*="/cart/add"] button[name="add"]');
        const form = button?.form;
        const variantInput = form?.querySelector('[name="id"]');
        if (!button || !form || !this.productGridSection?.contains(form) || !variantInput?.value) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        button.dataset.variantId = variantInput.value;
        this.addItem(button);
      }

      async continueToCheckout(link) {
        link.setAttribute('aria-disabled', 'true');
        link.classList.add('loading');
        try {
          if (window.EasyMeepleBoxSynchronizer) {
            const result = await window.EasyMeepleBoxSynchronizer.requestSync('wizard-checkout');
            if (result?.cart) {
              this.cart = result.cart;
              this.summary = this.summarizeCart(result.cart);
              this.renderCartState();
            }
          }
          window.EasyMeepleAnalytics?.capture('checkout_clicked', { mode: this.config.mode, game_count: this.summary?.gameCount, warning_count: this.summary?.warnings.length });
          window.location.assign(link.href);
        } catch (error) {
          const status = this.querySelector('[data-wizard-status]');
          if (status) status.textContent = error.message || this.config.boxSyncError;
          console.error(error);
        } finally {
          link.removeAttribute('aria-disabled');
          link.classList.remove('loading');
        }
      }

      moveStep(offset) {
        const currentIndex = Math.max(0, this.availableSteps.indexOf(this.currentStep));
        const nextIndex = Math.min(this.availableSteps.length - 1, Math.max(0, currentIndex + offset));
        this.setStep(this.availableSteps[nextIndex]);
      }

      advanceAfterWizardAdd(variantId) {
        if (this.config.mode !== 'new_customer') return;

        const normalizedVariantId = String(variantId || '');
        if (this.currentStep === 1 && this.config.passVariantIds.includes(normalizedVariantId)) {
          this.setStep(2);
        } else if (this.currentStep === 2 && normalizedVariantId === this.config.depositVariantId) {
          this.setStep(3);
        }
      }

      changePicksQuantity(delta) {
        const input = this.querySelector('[data-picks-quantity]');
        if (!input || !this.summary) return;

        const remaining = Math.max(0, this.config.maxGames - this.summary.gameCount);
        const next = Math.min(remaining, Math.max(1, (Number(input.value) || 1) + delta));
        input.value = remaining > 0 ? next : 0;
        this.renderPicksState();
      }

      async addPicks(button) {
        const select = this.querySelector('[data-picks-variant]');
        const quantityInput = this.querySelector('[data-picks-quantity]');
        const quantity = Number(quantityInput?.value) || 0;
        if (!select || quantity < 1) return;

        button.dataset.variantId = select.value;
        const added = await this.addItem(button, quantity);
        if (added) this.querySelector('[data-picks]')?.removeAttribute('open');
      }

      async addItem(button, quantity = 1) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return false;

        const errorNode = button.closest('[data-product-id]')?.querySelector('[data-add-error]');
        const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const cartDrawerWasOpen = Boolean(cartDrawer?.classList.contains('active'));
        this.suppressCartDrawerAutoOpen(cartDrawer, cartDrawerWasOpen);
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');
        if (errorNode) {
          errorNode.hidden = true;
          errorNode.textContent = '';
        }

        const item = { id: Number(button.dataset.variantId), quantity: Math.max(1, Number(quantity) || 1) };
        if (button.dataset.sellingPlan) item.selling_plan = Number(button.dataset.sellingPlan);
        const formData = new FormData();
        formData.append('id', String(item.id));
        formData.append('quantity', String(item.quantity));
        if (item.selling_plan) formData.append('selling_plan', String(item.selling_plan));
        const drawerSectionIds = cartDrawer?.getSectionsToRender?.().map((section) => section.id) || [];

        let added = false;
        try {
          const requestConfig = typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { Accept: 'application/json' } };
          requestConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete requestConfig.headers['Content-Type'];
          requestConfig.body = formData;

          const response = await fetch(window.routes.cart_add_url, requestConfig);
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || this.config.cartAddError);
          const variantId = String(item.id);
          const eventName = this.config.passVariantIds.includes(variantId) ? 'meeple_pass_selected'
            : variantId === this.config.depositVariantId ? 'deposit_added'
            : this.config.placeholderVariantIds.includes(variantId) ? 'easymeeple_picks_selected' : null;
          if (eventName) window.EasyMeepleAnalytics?.capture(eventName, {
            mode: this.config.mode, variant_id: variantId, product_id: String(result.product_id || ''),
            product_title: result.product_title, variant_title: result.variant_title,
            quantity: item.quantity, selling_plan_id: item.selling_plan ? String(item.selling_plan) : null
          });


          if (window.EasyMeepleBoxSynchronizer) {
            await window.EasyMeepleBoxSynchronizer.requestSync('wizard-add');
          }

          if (cartDrawer && drawerSectionIds.length) {
            await this.refreshCartDrawerSilently(cartDrawer, result, drawerSectionIds, item.id);
          }
          if (cartDrawer && !cartDrawerWasOpen) {
            cartDrawer.close?.();
            requestAnimationFrame(() => cartDrawer.close?.());
            setTimeout(() => cartDrawer.close?.(), 150);
          }
          this.advanceAfterWizardAdd(String(item.id));
          added = true;
        } catch (error) {
          if (errorNode) {
            errorNode.textContent = error.message || this.config.cartAddError;
            errorNode.hidden = false;
          }
          console.error(error);
        } finally {
          button.classList.remove('loading');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
          if (button.disabled) button.setAttribute('aria-disabled', 'true');
          else button.removeAttribute('aria-disabled');
        }
        return added;
      }

      suppressCartDrawerAutoOpen(cartDrawer, cartDrawerWasOpen) {
        if (!cartDrawer || cartDrawerWasOpen || typeof MutationObserver === 'undefined') return;

        const keepClosed = () => {
          if (cartDrawer.classList.contains('active')) cartDrawer.close?.();
        };
        const observer = new MutationObserver(keepClosed);
        observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class', 'open'] });
        keepClosed();
        setTimeout(() => {
          keepClosed();
          observer.disconnect();
        }, 5000);
      }

      async refreshCartDrawerSilently(cartDrawer, addResult, sectionIds, productId) {
        let drawerState = addResult;

        try {
          const root = window.Shopify?.routes?.root || '/';
          const params = new URLSearchParams({ sections: sectionIds.join(',') });
          const response = await fetch(`${root}?${params.toString()}`, {
            headers: { Accept: 'application/json' },
          });
          const sections = await response.json();
          const hasFreshSections = response.ok && sectionIds.every(
            (sectionId) => typeof sections?.[sectionId] === 'string' && sections[sectionId].trim()
          );

          if (hasFreshSections) {
            drawerState = { ...addResult, id: addResult.id || productId, sections };
          }
        } catch (error) {
          console.warn('Could not refresh the cart drawer sections after adding an item.', error);
        }

        const hasRenderableSections = sectionIds.every(
          (sectionId) => typeof drawerState.sections?.[sectionId] === 'string' && drawerState.sections[sectionId].trim()
        );

        if (!hasRenderableSections) return;

        cartDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
        cartDrawer.productId = drawerState.id || productId;
        cartDrawer.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement || typeof drawerState.sections[section.id] !== 'string') return;
          sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(drawerState.sections[section.id], section.selector);
        });

        setTimeout(() => {
          const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
        });
      }

      setStep(step, shouldScroll = true) {
        const requestedStep = Number(step);
        this.currentStep = this.availableSteps.includes(requestedStep) ? requestedStep : this.availableSteps[0];
        sessionStorage.setItem(this.storageKey, String(this.currentStep));

        this.panels.forEach((panel) => {
          panel.hidden = Number(panel.dataset.wizardPanel) !== this.currentStep;
        });

        this.progressButtons.forEach((button) => {
          const active = Number(button.dataset.wizardStep) === this.currentStep;
          if (active) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
        });

        if (this.productGridSection) this.productGridSection.hidden = this.currentStep !== 3;

        if (this.currentStep === 4) this.renderReview();
        if (this.currentStep === 1) this.schedulePassCardRowSync();

        window.EasyMeepleAnalytics?.wizard(this);

        if (shouldScroll) {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      renderCartState() {
        if (!this.summary) return;

        this.querySelectorAll('[data-game-count]').forEach((node) => {
          node.textContent = `${this.summary.gameCount}/${this.config.maxGames}`;
        });

        const counterMessage = this.querySelector('[data-game-counter-message]');
        if (counterMessage) {
          if (this.summary.gameCount === 0) counterMessage.textContent = this.config.counterEmpty;
          else if (this.summary.gameCount <= this.config.maxGames) counterMessage.textContent = this.config.counterValid;
          else counterMessage.textContent = this.config.counterOver;
        }

        this.renderStepThreeBoxPrice();
        this.updateProductState(this.config.passProductIds, this.summary.passCount, '[data-pass-card]');
        this.updateProductState([this.config.depositProductId], this.summary.depositCount, '[data-deposit-card]');
        this.renderPicksState();
        this.schedulePassCardRowSync();

        if (this.currentStep === 4) this.renderReview();
      }

      renderStepThreeBoxPrice() {
        const wrapper = this.querySelector('[data-step-three-box-price]');
        const value = this.querySelector('[data-step-three-box-price-value]');
        if (!wrapper || !value || !this.summary) return;

        const desiredVariantId = String(this.config.boxVariants[String(this.summary.gameCount)] || '');
        const boxItem = this.summary.boxLines.find(
          (item) => String(item.variant_id) === desiredVariantId && Number(item.quantity) === 1
        );

        if (!boxItem || this.summary.gameCount < 1 || this.summary.gameCount > this.config.maxGames) {
          wrapper.hidden = true;
          value.replaceChildren();
          return;
        }

        value.replaceChildren(this.createReviewPrice(boxItem, { box: true }));
        wrapper.hidden = false;
      }

      updateProductState(productIds, count, selector) {
        this.querySelectorAll(selector).forEach((card) => {
          const inCart = productIds.includes(String(card.dataset.productId)) && count > 0;
          card.classList.toggle('is-in-cart', inCart);
          const button = card.querySelector('[data-wizard-add]');
          if (!button) return;

          const thisProductInCart = this.cart?.items.some(
            (item) => String(item.product_id) === String(card.dataset.productId) && Number(item.quantity) > 0
          );
          button.disabled = Boolean(thisProductInCart);
          button.setAttribute('aria-disabled', thisProductInCart ? 'true' : 'false');
          const label = button.querySelector('[data-button-label]');
          if (label) label.textContent = thisProductInCart ? this.config.inCartLabel : this.config.addLabel;
        });
      }

      renderPicksState() {
        const picks = this.querySelector('[data-picks]');
        if (!picks || !this.cart || !this.summary) return;

        const picksQuantity = this.cart.items.reduce((total, item) => (
          this.config.placeholderVariantIds.includes(String(item.variant_id))
            ? total + (Number(item.quantity) || 0)
            : total
        ), 0);
        const remaining = Math.max(0, this.config.maxGames - this.summary.gameCount);
        const badge = picks.querySelector('[data-picks-cart-quantity]');
        const input = picks.querySelector('[data-picks-quantity]');
        const select = picks.querySelector('[data-picks-variant]');
        const addButton = picks.querySelector('[data-picks-add]');
        const minus = picks.querySelector('[data-picks-quantity-change="-1"]');
        const plus = picks.querySelector('[data-picks-quantity-change="1"]');

        if (badge) {
          badge.textContent = `${this.config.quantityLabel}: ${picksQuantity}`;
          badge.hidden = picksQuantity === 0;
        }

        if (input) {
          input.max = String(remaining);
          input.value = remaining > 0 ? String(Math.min(remaining, Math.max(1, Number(input.value) || 1))) : '0';
        }

        if (minus) minus.disabled = remaining === 0 || Number(input?.value) <= 1;
        if (plus) plus.disabled = remaining === 0 || Number(input?.value) >= remaining;
        if (addButton) {
          const selectedAvailable = Boolean(select?.selectedOptions[0] && !select.selectedOptions[0].disabled);
          addButton.disabled = remaining === 0 || !selectedAvailable;
          addButton.setAttribute('aria-disabled', addButton.disabled ? 'true' : 'false');
        }
      }

      schedulePassCardRowSync() {
        if (this.passCardSyncFrame) cancelAnimationFrame(this.passCardSyncFrame);
        this.passCardSyncFrame = requestAnimationFrame(() => {
          this.passCardSyncFrame = null;
          this.syncPassCardRows();
        });
      }

      syncPassCardRows() {
        const cards = Array.from(this.querySelectorAll('[data-pass-card]'));
        if (cards.length < 2) return;

        [
          '.easymeeple-wizard__choice-title',
          '.easymeeple-wizard__choice-price',
          '.easymeeple-wizard__choice-note',
          '.easymeeple-wizard__choice .button',
        ].forEach((selector) => {
          const items = cards.map((card) => card.querySelector(selector)).filter(Boolean);
          items.forEach((item) => { item.style.minHeight = ''; });
          const largestHeight = Math.max(...items.map((item) => Math.ceil(item.getBoundingClientRect().height)));
          items.forEach((item) => { item.style.minHeight = `${largestHeight}px`; });
        });
      }

      renderReview() {
        if (!this.cart || !this.summary) return;
        const list = this.querySelector('[data-cart-list]');
        const warnings = this.querySelector('[data-warning-list]');
        const total = this.querySelector('[data-cart-total]');

        list.innerHTML = '';
        if (this.cart.items.length === 0) {
          const empty = document.createElement('li');
          empty.className = 'easymeeple-wizard__empty';
          empty.textContent = this.config.emptyCart;
          list.appendChild(empty);
        } else {
          const review = this.getReviewGroups(this.cart);
          review.otherItems.forEach((item) => {
            list.appendChild(this.createReviewLine(item));
          });

          if (review.boxItems.length || review.gameItems.length) {
            const boxItem = review.boxItems[0];
            const boxRow = document.createElement('li');
            boxRow.className = 'easymeeple-wizard__cart-line easymeeple-wizard__cart-line--box';

            const boxDescription = document.createElement('div');
            const boxTitle = document.createElement('div');
            boxTitle.className = 'easymeeple-wizard__cart-line-title';
            boxTitle.textContent = boxItem?.product_title || this.config.boxLabel;
            const boxMeta = document.createElement('div');
            boxMeta.className = 'easymeeple-wizard__cart-line-meta';
            const gameLabel = review.gameCount === 1 ? this.config.gameSingularLabel : this.config.gamePluralLabel;
            boxMeta.textContent = `${review.gameCount} ${gameLabel}`;
            boxDescription.append(boxTitle, boxMeta);

            const boxPrice = this.createReviewPrice(boxItem, { box: true });
            boxRow.append(boxDescription, boxPrice);
            list.appendChild(boxRow);

            review.gameItems.forEach((item) => {
              list.appendChild(this.createReviewLine(item, { child: true, showPrice: false }));
            });

            review.boxItems.slice(1).forEach((item) => {
              list.appendChild(this.createReviewLine(item));
            });
          }
        }

        total.textContent = this.formatMoney(this.cart.total_price);
        warnings.innerHTML = '';

        if (this.summary.warnings.length === 0) {
          const ok = document.createElement('li');
          ok.className = 'easymeeple-wizard__success';
          ok.textContent = this.config.reviewSuccess;
          warnings.appendChild(ok);
        } else {
          this.summary.warnings.forEach((message) => {
            const item = document.createElement('li');
            item.className = 'easymeeple-wizard__warning';
            item.textContent = message;
            warnings.appendChild(item);
          });
        }
      }

      getReviewGroups(cart) {
        const gameItems = [];
        const boxItems = [];
        const otherItems = [];
        let gameCount = 0;

        cart.items.forEach((item) => {
          if (this.isGame(item)) {
            gameItems.push(item);
            gameCount += Number(item.quantity) || 0;
          } else if (String(item.product_id) === this.config.boxProductId) {
            boxItems.push(item);
          } else {
            otherItems.push(item);
          }
        });

        return { gameItems, boxItems, otherItems, gameCount };
      }

      createReviewLine(item, options = {}) {
        const row = document.createElement('li');
        row.className = `easymeeple-wizard__cart-line${options.child ? ' easymeeple-wizard__cart-line--child' : ''}`;

        const description = document.createElement('div');
        const title = document.createElement('div');
        title.className = 'easymeeple-wizard__cart-line-title';
        title.textContent = item.product_title;
        const meta = document.createElement('div');
        meta.className = 'easymeeple-wizard__cart-line-meta';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? ` · ${item.variant_title}` : '';
        meta.textContent = `${this.config.quantityLabel}: ${item.quantity}${variant}`;
        description.append(title, meta);
        row.appendChild(description);

        if (options.showPrice !== false) {
          row.appendChild(this.createReviewPrice(item));
        }

        return row;
      }

      createReviewPrice(item, options = {}) {
        const pricing = item ? this.getLinePricing(item) : { original: 0, final: 0 };
        const price = document.createElement('div');
        price.className = `easymeeple-wizard__cart-line-price${options.box ? ' easymeeple-wizard__cart-line-price--box' : ''}`;

        if (pricing.final < pricing.original) {
          const original = document.createElement('s');
          original.className = 'easymeeple-wizard__cart-line-price-original';
          original.textContent = this.formatMoney(pricing.original);
          const final = document.createElement('strong');
          final.className = 'easymeeple-wizard__cart-line-price-final';
          final.textContent = this.formatMoney(pricing.final);
          price.append(original, final);
        } else {
          price.textContent = this.formatMoney(pricing.final);
        }

        return price;
      }

      getLinePricing(item) {
        const quantity = Number(item.quantity) || 0;
        const fallbackOriginal = (Number(item.original_price) || Number(item.price) || 0) * quantity;
        const original = Number.isFinite(Number(item.original_line_price))
          ? Number(item.original_line_price)
          : fallbackOriginal;
        const fallbackFinal = Number.isFinite(Number(item.line_price)) ? Number(item.line_price) : original;
        const final = Number.isFinite(Number(item.final_line_price)) ? Number(item.final_line_price) : fallbackFinal;
        return { original, final };
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: this.cart.currency || this.config.currency,
          }).format((Number(cents) || 0) / 100);
        } catch (_) {
          return `${((Number(cents) || 0) / 100).toFixed(2)} ${this.cart.currency || this.config.currency}`;
        }
      }
    }
  );
}

